import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  AcademicCapIcon,
  PlayIcon,
  UserGroupIcon,
  VideoCameraIcon,
  DocumentIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { LearningSection, LearningContent, LearningContentType } from '../types';
import { learningAPI } from '../services/api';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ContentViewer, { ContentType } from '../components/learning/ContentViewer';
import toast from 'react-hot-toast';

const UserLearning: React.FC = () => {
  const [sections, setSections] = useState<LearningSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContent, setSelectedContent] = useState<LearningContent | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      setIsLoading(true);
      const data = await learningAPI.getMyLearningSections();
      setSections(data);
    } catch (error) {
      console.error('Error loading learning sections:', error);
      toast.error('Failed to load learning sections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContentView = (content: LearningContent) => {
    setSelectedContent(content);
    setIsViewerOpen(true);
  };

  const getContentIcon = (contentType: LearningContentType) => {
    switch (contentType) {
      case LearningContentType.VIDEO:
        return <PlayIcon className="h-5 w-5 text-primary-600" />;
      case LearningContentType.PDF:
        return <DocumentIcon className="h-5 w-5 text-red-600" />;
      case LearningContentType.IMAGE:
        return <PhotoIcon className="h-5 w-5 text-green-600" />;
      default:
        return <PlayIcon className="h-5 w-5 text-primary-600" />;
    }
  };

  const getContentTypeLabel = (contentType: LearningContentType) => {
    switch (contentType) {
      case LearningContentType.VIDEO:
        return 'Video';
      case LearningContentType.PDF:
        return 'PDF';
      case LearningContentType.IMAGE:
        return 'Image';
      default:
        return 'Content';
    }
  };

  const filteredSections = sections.filter(section =>
    section.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.team.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AcademicCapIcon className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Learning Center</h1>
              <p className="text-gray-600">Access learning content from your teams</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search learning sections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Learning Sections */}
        {filteredSections.length === 0 ? (
          <div className="text-center py-12">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No learning content available</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'No sections match your search.' : 'No learning sections have been created for your teams yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSections.map((section) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Section Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {section.name}
                      </h3>
                      {section.description && (
                        <p className="text-sm text-gray-600 mb-3">
                          {section.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <UserGroupIcon className="h-4 w-4" />
                          <span>{section.team.name}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <AcademicCapIcon className="h-4 w-4" />
                          <span>{section._count.content || section._count.videos || 0} items</span>
                        </div>
                      </div>
                    </div>
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: section.team.color }}
                    />
                  </div>
                </div>

                {/* Content List */}
                <div className="p-6">
                  {(!section.content || section.content.length === 0) && (!section.videos || section.videos.length === 0) ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No content in this section yet
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {/* Display new content items */}
                      {section.content && section.content.map((content: any) => (
                        <div
                          key={content.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => handleContentView(content)}
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            <div className="flex-shrink-0">
                              {getContentIcon(content.contentType)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {content.title}
                              </p>
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                <span className="px-2 py-1 bg-gray-200 rounded-full">
                                  {getContentTypeLabel(content.contentType)}
                                </span>
                                <ClockIcon className="h-3 w-3" />
                                <span>
                                  {new Date(content.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Display legacy videos for backward compatibility */}
                      {section.videos && section.videos.map((video: any) => (
                        <div
                          key={video.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => handleContentView({
                            id: video.id,
                            title: video.title,
                            contentType: LearningContentType.VIDEO,
                            url: video.url,
                            sectionId: section.id,
                            section: section,
                            createdAt: video.createdAt,
                            updatedAt: video.updatedAt,
                          })}
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            <div className="flex-shrink-0">
                              <PlayIcon className="h-5 w-5 text-primary-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {video.title}
                              </p>
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                <span className="px-2 py-1 bg-gray-200 rounded-full">
                                  Video
                                </span>
                                <ClockIcon className="h-3 w-3" />
                                <span>
                                  {new Date(video.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Content Viewer Modal */}
        <ContentViewer
          isOpen={isViewerOpen}
          onClose={() => {
            setIsViewerOpen(false);
            setSelectedContent(null);
          }}
          content={selectedContent}
        />
      </div>
    </Layout>
  );
};

export default UserLearning;
